import type { ReactElement } from 'react';

import type { ButtonProps, SubmitButtonProps } from '@capsule/consumer-design-system';
import { Typography, Button, SubmitButton } from '@capsule/consumer-design-system';

import type { AnimationProps } from 'framer-motion';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

const CTAFooter = styled(motion.footer)`
  position: fixed;
  inset: 0;
  top: unset;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.space.s5};
  flex-shrink: 0;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.transparent} 0%,
    rgba(255, 255, 255, 0.4) 8.85%,
    rgba(255, 255, 255, 0.7) 21.35%,
    ${({ theme }) => theme.colors.white} 50%,
    ${({ theme }) => theme.colors.white} 100%
  );
  z-index: 1;
  width: 100%;
  & > * {
    width: 100%;
  }

  ${({ theme }) => theme.mediaQueries.small} {
    z-index: unset;
    position: static;
    inset: unset;
    background: none;
    box-shadow: none;
    height: auto;
    padding: 0;
    align-items: flex-start;
    padding-bottom: ${({ theme }) => theme.space.s6};
  }
`;

const ErrorContainer = styled(Typography).attrs({ variant: 'small' })`
  color: ${({ theme }) => theme.colors.capsuleRed60};
  text-align: center;

  transition: all ${({ theme }) => theme.animation.typical};
  height: 1.6rem;
  opacity: 1;
  margin-bottom: ${({ theme }) => theme.space.s3};

  &:empty {
    transition: height 0.2s linear;
    height: 0;
    opacity: 0;
    margin-bottom: 0;
  }
`;

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

type WithOptionalVariantProp<T extends { variant: any }> = PartialBy<T, 'variant'>;

type CTAWithRequiredClickHandlerProp = WithOptionalVariantProp<Omit<ButtonProps, 'onClick'>> &
  Required<Pick<ButtonProps, 'onClick'>>;

export interface LayoutCTAGroupProps extends AnimationProps {
  className?: string;
  cta1: WithOptionalVariantProp<ButtonProps> | WithOptionalVariantProp<SubmitButtonProps>;
  cta2?: CTAWithRequiredClickHandlerProp | ReactElement;
  error?: string;
}

export const LayoutCTAGroup = ({ className, cta1, cta2, error, ...animationProps }: LayoutCTAGroupProps) => (
  <CTAFooter className={className} {...animationProps}>
    <ErrorContainer role="alert">{error}</ErrorContainer>

    {'isSubmitting' in cta1 ? (
      <SubmitButton {...cta1} variant={cta1.variant ?? 'contained-secondary-lg'} />
    ) : (
      <Button {...cta1} variant={cta1.variant ?? 'contained-secondary-lg'} />
    )}

    {cta2 && 'onClick' in cta2 && (
      <Button
        {...cta2}
        variant={cta2.variant ?? 'outline-lg'}
        css={css`
          margin-top: ${({ theme }) => theme.space.s3};
        `}
      />
    )}

    {cta2 && !('onClick' in cta2) && cta2}
  </CTAFooter>
);
